import type { ProductPropertiesKeyAPI } from '~/api/Catalog';
import { company } from '~/company/Company';
import type { ProductState } from '~/stores/CategoriesStore';
import type {
  ProductFilterChoice,
  ProductPropertiesValue,
} from '~/types/Product/interface';

import { useMounted } from './useMounted';
import { getProductLimit } from './useProductLimit';

const isBlank = (value: unknown) => value == null || value === '';

/** An offer the selector can switch to: loaded in full, or known only from the compact `variants[]`. */
interface OfferCandidate {
  sku: string;
  limit: number;
  valueOf: (code: ProductPropertiesKeyAPI) => unknown;
}

/**
 * The selector keeps the last matching candidate, so loaded offers go last:
 * a value the user already switched to resolves to the same offer again
 * instead of another offer sharing the combination.
 */
const getOfferCandidates = (
  product: ProductState,
  mounted: boolean,
): OfferCandidate[] => {
  const offers = product.data?.offers ?? [];
  const loadedSkus = new Set(offers.map(({ sku }) => sku));

  return [
    ...(product.data?.variantOffers ?? [])
      .filter(({ sku }) => !loadedSkus.has(sku))
      .map((variant) => ({
        sku: variant.sku,
        limit: getProductLimit(variant, mounted).limit,
        valueOf: (code: ProductPropertiesKeyAPI) => variant.values[code],
      })),
    ...offers.map((offer) => ({
      sku: offer.sku,
      limit: getProductLimit(offer, mounted).limit,
      valueOf: (code: ProductPropertiesKeyAPI) =>
        offer.selectableProperties[code]?.value,
    })),
  ];
};

export const useProductChoices = ({
  product,
}: {
  product?: ProductState;
}): Record<string, ProductPropertiesValueExtended> => {
  const mounted = useMounted();

  if (!product) {
    return {};
  }

  const productFilters = product?.selectedOffer?.selectableProperties;
  const selectedProperties = Object.values(
    product?.selectedOffer?.selectableProperties ?? {},
  ).reduce(
    (
      acc: Record<string, string | number | boolean | string[]>,
      item: ProductPropertiesValue,
    ) => {
      acc[item.code] = item.value;
      return acc;
    },
    {},
  );
  const candidates = getOfferCandidates(product, mounted);

  return Object.fromEntries(
    Object.entries(productFilters ?? {}).map(([key, value]) => {
      const choices = value.choices.map((choice) => {
        let sku = '',
          sellable = false,
          selected = false,
          fallbackSku = '',
          fallbackSellable = false;

        if (
          choice.label ===
          product?.selectedOffer?.selectableProperties[
            key as ProductPropertiesKeyAPI
          ]?.value
        ) {
          selected = true;
        }

        for (const offer of candidates) {
          if (choice.label === offer.valueOf(key as ProductPropertiesKeyAPI)) {
            let ok = true;
            Object.entries(selectedProperties).forEach(
              ([selectedKey, selectedValue]) => {
                if (selectedKey !== key) {
                  const offerValue = offer.valueOf(
                    selectedKey as ProductPropertiesKeyAPI,
                  );

                  // Missing property value acts as a wildcard: offers are not
                  // required to define every selectable property
                  if (
                    !isBlank(selectedValue) &&
                    !isBlank(offerValue) &&
                    selectedValue !== offerValue
                  ) {
                    ok = false;
                  }
                }
              },
            );

            if (ok) {
              sku = offer.sku;

              if (offer.limit > 0) {
                sellable = true;
              }
            } else if (!fallbackSku || (offer.limit > 0 && !fallbackSellable)) {
              // No offer matches this value together with the other selected
              // properties — remember an offer carrying this value anyway
              // (preferring an in-stock one), so the variant stays selectable
              // and switches the rest of the selection to an existing combo.
              fallbackSku = offer.sku;
              fallbackSellable = offer.limit > 0;
            }
          }
        }

        const targetSku =
          sku || (company.variantSwatchesEnabled ? fallbackSku : '');

        return {
          ...choice,
          sku: targetSku,
          sellable,
          selected,
          combinationExists: Boolean(sku),
          targetSellable: sku
            ? sellable
            : Boolean(targetSku) && fallbackSellable,
        };
      });

      return [key, { ...value, choices }];
    }),
  );
};

export interface ProductFilterChoiceExtended extends ProductFilterChoice {
  sku: string;
  sellable: boolean;
  selected: boolean;
  combinationExists: boolean;
  targetSellable: boolean;
}

export interface ProductPropertiesValueExtended
  extends Omit<ProductPropertiesValue, 'choices'> {
  choices: ProductFilterChoiceExtended[];
}
